import { CommonModule } from '@angular/common';
import {
    AbstractControl,
    FormArray,
    FormBuilder,
    FormGroup,
    ReactiveFormsModule,
    Validators
} from '@angular/forms';
import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    inject
} from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { CbInfoMessageModule, CbTableModule } from 'cb-hub-lib';
import { Subscription } from 'rxjs';
import { IDocumentEntityMappedItem } from '@app/logic/documents';
import { ILotMappedItem } from '@app/logic/lots';
import { IDocAssignmentRow } from '../lot-migrator.models';

@Component({
    selector: 'cb-lot-migrator-document-assignments',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatSelectModule,
        CbTableModule,
        CbInfoMessageModule
    ],
    templateUrl: './lot-migrator-document-assignments.component.html',
    styleUrls: ['./lot-migrator-document-assignments.component.scss']
})
export class LotMigratorDocumentAssignmentsComponent implements OnChanges, OnDestroy, OnInit {
    @Input() public lots: ILotMappedItem[] = [];
    @Input() public lotNumbers: number[] = [];
    @Input() public lotSelectedDocuments: { [lotId: number]: IDocumentEntityMappedItem[] } = {};
    @Input() public sourceToDestinationLotNumberMap: Record<number, number> = {};
    @Output() public assignmentsChange = new EventEmitter<IDocAssignmentRow[]>();
    @Output() public assignmentsFormChange = new EventEmitter<FormArray>();

    private readonly _formBuilder = inject(FormBuilder);
    private assignmentsSub: Subscription = Subscription.EMPTY;
    public readonly displayedColumns = ['From Lot #', 'Job #', 'Document', 'Assign to Lot #'];
    public docAssignmentsFG = this._formBuilder.group({
        assignments: this._formBuilder.array([])
    });

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['lotSelectedDocuments'] || changes['sourceToDestinationLotNumberMap']) {
            this.rebuildAssignmentForm();
        }
    }

    public ngOnInit(): void {
        this.assignmentsSub = this.docAssignmentsForm.valueChanges.subscribe(() => {
            this.assignmentsChange.emit(this.getCurrentAssignments());
        });
    }

    public ngOnDestroy(): void {
        this.assignmentsSub.unsubscribe();
    }

    public get docAssignmentsForm(): FormArray {
        return this.docAssignmentsFG.get('assignments') as FormArray;
    }

    public get docAssignments(): AbstractControl<any, any, any>[] {
        return this.docAssignmentsForm.controls;
    }

    public rebuildAssignmentForm(): void {
        this.docAssignmentsForm.clear();

        for (const [lotIdStr, items] of Object.entries(this.lotSelectedDocuments)) {
            const lotId = Number(lotIdStr);

            for (const item of items) {
                this.docAssignmentsForm.push(this.makeAssignmentRow(item, lotId));
            }
        }

        this.docAssignmentsForm.updateValueAndValidity();
        this.assignmentsFormChange.emit(this.docAssignmentsForm);
        this.assignmentsChange.emit(this.getCurrentAssignments());
    }

    public getCurrentAssignments(): IDocAssignmentRow[] {
        return this.docAssignmentsForm.getRawValue() as IDocAssignmentRow[];
    }

    private makeAssignmentRow(item: IDocumentEntityMappedItem, sourceLotId: number): FormGroup {
        const meta = this.getLotMetaById(sourceLotId);
        const mappedDestinationLotNumber = this.resolveMappedDestinationLotNumber(sourceLotId);
        const documentName =
            item?.document?.name ??
            item?.document?.filename ??
            String(item?.document?.id ?? item?.id ?? '');
        const documentId = Number(item?.document?.id ?? item?.id);
        const documentEntityId = Number(item?.id);
        const documentType = this.resolveDocumentType(item);

        return this._formBuilder.group<IDocAssignmentRow>({
            sourceLotId: this._formBuilder.control(sourceLotId),
            sourceLotNumber: this._formBuilder.control(meta.lotNumber),
            sourceJobNumber: this._formBuilder.control(meta.jobNumber),
            documentEntityId: this._formBuilder.control(documentEntityId),
            documentId: this._formBuilder.control(documentId),
            documentName: this._formBuilder.control(documentName),
            documentType: this._formBuilder.control(documentType),
            destinationLotNumber: this._formBuilder.control(mappedDestinationLotNumber, {
                validators: [Validators.required]
            })
        } as any);
    }

    private resolveMappedDestinationLotNumber(sourceLotId: number): number | undefined {
        return this.sourceToDestinationLotNumberMap[sourceLotId];
    }

    private getLotMetaById(lotId: number): { lotNumber?: number; jobNumber?: string } {
        const lot = this.lots.find(x => x.id === lotId);

        return {
            lotNumber: lot?.lotNumber,
            jobNumber: lot?.jobNumber
        };
    }

    private resolveDocumentType(item: IDocumentEntityMappedItem): string {
        const mappedDocument = item?.document as any;
        const mappedItem = item as any;

        return (
            mappedDocument?.documentType?.label ??
            mappedDocument?.type?.label ??
            mappedDocument?.type ??
            mappedItem?.documentType?.label ??
            mappedItem?.type ??
            ''
        );
    }
}
